import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import Tooltip from '@components-clean/core/tooltip';
import {
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import {
    DELETE_RISK_OBSERVATION_ACTION,
    GET_COMPLETE_RISKS_RISK_LOG,
    GET_RISK_OBSERVATIONS_AND_ACTIONS,
} from '@queries/risk-assessment';

export interface ActionDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    observation: ExtendedRiskObservationFields;
    action: ExtendedRiskActionFields;
    riskId: string;
    studyId: string;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteActionDialog = ({ onClose, onSubmit, observation, action, riskId, studyId }: ActionDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.deleteAction' });
    const { t: tCommon } = useTranslation('common');
    const [deleteRiskActionMutation] = useMutation(DELETE_RISK_OBSERVATION_ACTION);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const onDelete = async (data: FormInputs) => {
        deleteRiskActionMutation({
            variables: {
                observationId: observation.id,
                actionId: action.id,
                reason: data.deleteReason,
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISKS_RISK_LOG,
                    variables: {
                        studyId,
                    },
                },
                {
                    query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId,
                        riskId,
                    },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.deleteRiskObservationAction) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };
    const description = observation.description ?? '';
    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {description.length <= 300 ? (
                <>
                    <span className="k-font-medium">{t('warningMessage')} </span> {description}
                </>
            ) : (
                <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                    <span className="k-font-medium">{t('warningMessage')}</span>{' '}
                    <span title={description}>{`${description.slice(0, 300)}...`}</span>
                </Tooltip>
            )}
        </span>
    );
    return (
        <DraggableDialog
            dataTestId="delete-action-warning"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={`${t('title')}: ${action.recordId}`}
            initialWidth={530}
            initialHeight={280}
            height={null}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <InfoField type="Warning" message={warningText} />

                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="deleteReason"
                    required
                    data-testid="field-action-deleteReason"
                    label={`${t('deleteReasonLabel')} *`}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteActionDialog;
